import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Image,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { ExclamationCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";

import {
  HEADERS_EXPORT_DETAIL,
  POST_CHECK_DOC_IMPOUND,
  POST_DATA_CHECK_SALE_IMPOUND,
  POST_DETAIL_SELLING_CAR_IMPOUND,
} from "../../../API/apiUrls";
import CurrencyFormat from "../../../../hook/CurrencyFormat";
import ExportPDF from "../report/ExportPDF";
import MoneyInput from "../../afterCourt/componentsEditJudgment/MoneyInput";
import LoadCompanies from "../../../../hook/LoadCompanies";

const { Text, Title } = Typography;
const { TextArea } = Input;

// ─── Constants ────────────────────────────────────────────────────
const COMPANY = {
  name: "บริษัท วัน ลีซิ่ง จำกัด (สำนักงานใหญ่)",
  address: "ที่อยู่ 89 หมู่ 14 ต.ในเมือง อ.เมืองขอนแก่น จ.ขอนแก่น 40000",
  taxId: "0405559001925",
};

const cleanChassis = (s) =>
  String(s ?? "")
    .replace(/^[\u0e30-\u0e4e]+/, "")
    .trim() || "-";

const toNum = (v) => Number(String(v ?? "").replace(/,/g, "")) || 0;
// ─── Main Modal ───────────────────────────────────────────────────
const InvoiceAndReceiptCarImpound = ({ open, close, record, handleLoad }) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [form] = Form.useForm();
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const { companiesOptions: allCompaniesOptions, loading: loadingCompanies } =
    LoadCompanies();
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailDB2, setDetailDB2] = useState(null);
  const [saving, setSaving] = useState(false);

  const isVat = useMemo(
    () => ["vsfhp", "vsfhp-h"].includes(detailDB2?.LOAN?.DATA_TYPE),
    [detailDB2],
  );

  // ── Load API ──────────────────────────────────────────────────
  const loadData = useCallback(
    async (signal) => {
      if (!record?.CONTNO) return;
      setLoading(true);
      try {
        const res = await axios.post(
          POST_DETAIL_SELLING_CAR_IMPOUND,
          { contractNo: [record?.CONTNO, record?.newContno].filter(Boolean) },
          { headers: HEADERS_EXPORT_DETAIL, signal },
        );
        if (!res?.data) {
          message.warning("ไม่พบข้อมูล");
          return;
        }
        const d = res.data.data?.[0] ?? null;
        const d2 = res.data.data_DB2?.[0] ?? null;
        setDetail(d);
        setDetailDB2(d2);

        form.setFieldsValue({
          invoice_no: d?.product_code || "",
          invoice_date: d?.sold_out_date ? dayjs(d.sold_out_date) : dayjs(),
          buyer_name: [d?.firstname, d?.lastname].filter(Boolean).join(" "),
          buyer_taxid: d?.idcard || "",
          buyer_address: d?.address || "",
          buyer_phone: d?.phone || "",
          // ✅ raw number ไม่ format
          sold_out_price: currencyFormatPoint(Number(d?.sold_out_price) || 0),
          sellAmount: currencyFormatPoint(
            (Number(d?.sold_out_price) || 0) / 1.07,
          ),
          vat: currencyFormatPoint(
            (Number(d?.sold_out_price) || 0) -
              (Number(d?.sold_out_price) || 0) / 1.07,
          ),

          mark: d?.mark || "",
        });
      } catch (error) {
        if (axios.isCancel(error) || error.name === "CanceledError") return;
        message.error(
          error.response?.data?.message || error.message || "เกิดข้อผิดพลาด",
        );
      } finally {
        setLoading(false);
      }
    },
    [record?.CONTNO, record?.newContno, form],
  );

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [open, loadData]);

  const companiesOptions = useMemo(() => {
    if (!allCompaniesOptions?.length) return [];
    return allCompaniesOptions;
  }, [allCompaniesOptions]);

  // ── Print ─────────────────────────────────────────────────────
  const handlePrint = async () => {
    try {
      const values = await form.validateFields();
      setPrinting(true);

      const pdfData = {
        company_name: COMPANY.name,
        company_address: COMPANY.address,
        company_taxid: COMPANY.taxId,
        invoice_no: values.doc_no,
        invoice_date: values.invoice_date?.toISOString(),
        cashbill_no: values.doc_no,
        contract_no: detail?.contract_no,
        buyer_name: values.buyer_name,
        buyer_address: values.buyer_address,
        buyer_taxid: values.buyer_taxid,
        brand: detailDB2?.MORTGAGE?.TYPE,
        model: detailDB2?.MORTGAGE?.MODEL,
        color: detail?.color || detailDB2?.MORTGAGE?.COLOR,
        chassis_no: cleanChassis(detail?.chassis_no),
        engine_no: detailDB2?.MORTGAGE?.ENGNO,
        sold_out_price: Number(values.sold_out_price) || 0, // ตอนนี้ได้ค่าถูกแล้ว
        receiver_name: values.receiver_name,
        is_vat: isVat, // ส่งไป PDF ด้วย
      };
      console.log("pdfData", pdfData);
      console.log("values", values);
      const companyInvoice =
        allCompaniesOptions?.find((c) => c.value === values.companyInvoice) ||
        {};

      const companyReceipt =
        allCompaniesOptions?.find((c) => c.value === values.companyReceipt) ||
        {};
      console.log("companyReceipt", companyReceipt);
      console.log("companyInvoice", companyInvoice);
      const doc = await ExportPDF(
        pdfData,
        values,
        companyInvoice,
        companyReceipt,
        record,
      );

      // เปิดใน tab ใหม่
      doc.output("dataurlnewwindow");
    } catch (err) {
      if (err?.errorFields) {
        message.warning("กรุณากรอกข้อมูลให้ครบ");
      } else {
        console.error("print error:", err);
        message.error("ไม่สามารถสร้าง PDF ได้");
      }
    } finally {
      setPrinting(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const companyInvoice =
        allCompaniesOptions?.find((c) => c.value === values.companyInvoice) ||
        {};

      const companyReceipt =
        allCompaniesOptions?.find((c) => c.value === values.companyReceipt) ||
        {};

      const pdfData = {
        company_name: COMPANY.name,
        company_address: COMPANY.address,
        company_taxid: COMPANY.taxId,
        invoice_no: values.doc_no,
        invoice_date: dayjs(values.invoice_date).format("YYYY-MM-DD"),
        cashbill_no: values.doc_no,
        contract_no: detail?.contract_no,
        buyer_name: values.buyer_name,
        buyer_address: values.buyer_address,
        buyer_taxid: values.buyer_taxid,
        engine_no: detailDB2?.MORTGAGE?.ENGNO,
        sold_out_price: toNum(values.sold_out_price),
        sellAmount: toNum(values.sellAmount),
        vat: toNum(values.vat),
        receiver_name: values.receiver_name,
        is_vat: isVat,
        img: detail.image_path,
        mark: values.mark,
        BAAB: detailDB2?.MORTGAGE?.BAAB,
        CC: detailDB2?.MORTGAGE?.CC,
        COLOR: detailDB2?.MORTGAGE?.COLOR,
        DORECV: detailDB2?.MORTGAGE?.DORECV,
        ENGNO: detailDB2?.MORTGAGE?.ENGNO,
        MANUYR: detailDB2?.MORTGAGE?.MANUYR,
        MILERT: detailDB2?.MORTGAGE?.MILERT,
        REGNO: detailDB2?.MORTGAGE?.REGNO,
        STRNO: detailDB2?.MORTGAGE?.STRNO,
        TYPE: detailDB2?.MORTGAGE?.TYPE,
        MODEL: detailDB2?.MORTGAGE?.MODEL,
        product_code: detail.product_code,
        tax_expiration_date: detail.tax_expiration_date,
        registration_date: detail.registration_date,
        phone: detail.phone,
        cost_price: detail.cost_price,
        DATA_TYPE: detailDB2?.LOAN?.DATA_TYPE,
        companyInvoice: companyInvoice,
        companyReceipt: companyReceipt,
        id_impound: record.id_impound,
        lawsuitId: record.lawsuitId,
        loanId: record.loanId,
        idJudge: record.idJudge,
        idJudgeDef: record.idJudgeDef,
        newContno: record.newContno,
        CONTNO: record.CONTNO,
        PAYTYP: null,
        ref: record.ref,
        userId: userId,
      };

      console.log("pdfData", pdfData);

      const dataStatus = { id_impound: record.id_impound, userId };

      const successPostSaveMongo = await postSaveMongo(pdfData);
      const successPostSaveStatus = await postSaveStatus(dataStatus);
      console.log("successPostSaveMongo", successPostSaveMongo);
      console.log("successPostSaveStatus", successPostSaveStatus);

      if (successPostSaveStatus && successPostSaveMongo) {
        setSaving(true);
        message.success("บันทึกข้อมูลสำเร็จ");
      }
    } catch (err) {
      if (err?.errorFields) {
        message.warning("กรุณากรอกข้อมูลให้ครบ");
      } else if (!err?.handled) {
        // เฉพาะ error ที่ยังไม่ได้แสดง message (กัน toast ซ้ำ)
        console.error("save error:", err);
        message.error("ไม่สามารถบันทึกข้อมูลได้");
      }
    } finally {
    }
  };

  const postSaveMongo = async (payload) => {
    try {
      const res = await axios.post(POST_DATA_CHECK_SALE_IMPOUND, payload);
      if (res.status !== 200) throw new Error(`HTTP ${res.status}`);
      return true;
    } catch (error) {
      showApiError(error);
      error.handled = true; // ทำเครื่องหมายว่าแสดง message แล้ว
      throw error;
    }
  };

  const postSaveStatus = async (payload) => {
    try {
      const res = await axios.post(POST_CHECK_DOC_IMPOUND, payload, {
        headers: HEADERS_EXPORT_DETAIL,
      });
      if (res.status !== 200) throw new Error(`HTTP ${res.status}`);

      return true;
    } catch (error) {
      showApiError(error);
      error.handled = true;
      throw error;
    }
  };

  // ── รวม error handler ของ axios ไว้ที่เดียว (เดิมเขียนซ้ำ 2 รอบ) ──
  const showApiError = (error) => {
    const status = error?.response?.status;
    const serverMsg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error.message;
    const map = {
      400: `ข้อมูลไม่ถูกต้อง: ${serverMsg}`,
      404: "ไม่พบปลายทาง (404)",
      500: "เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)",
    };
    message.error(
      map[status] ||
        `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
    );
  };

  const handleClose = () => {
    close(false);
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: "#faad14" }} />
          <span>ตรวจสอบข้อมูลการขายรถยึด</span>
        </Space>
      }
      open={open}
      onCancel={() => close(false)}
      footer={null}
      width={900}
      destroyOnHidden
    >
      <Spin spinning={loading} size="large" tip="Loading...">
        {/* ─── ข้อมูลสินค้า (อ่านอย่างเดียว) ─── */}
        <Divider>
          ข้อมูลสินค้า{" "}
          {detailDB2?.MORTGAGE?.BAAB?.includes("จักรยานยนต์") ? "🏍️" : "🚗"}
        </Divider>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[24, 8]}>
            <Col xs={24} md={6}>
              <Image
                src={detail?.image_path}
                alt="ภาพรถ"
                style={{
                  borderRadius: 8,
                  width: "100%",
                  aspectRatio: "4/3",
                  objectFit: "cover",
                }}
              />
              <div style={{ marginTop: 8, textAlign: "center" }}>
                <Space size={8} style={{ marginTop: 6 }}>
                  <Tag
                    color="gold"
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      padding: "2px 12px",
                      margin: 0,
                      textAlign: "center",
                      background: "#fff",
                    }}
                  >
                    {detailDB2?.MORTGAGE?.REGNO}
                    <br />
                    {detailDB2?.MORTGAGE?.DORECV}
                  </Tag>
                </Space>
              </div>
            </Col>
            <Col xs={24} md={18}>
              <Row gutter={[16, 6]}>
                <Col xs={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    เลขสัญญา
                  </Text>
                  <div>
                    <Text strong>{detail?.contract_no}</Text>
                  </div>
                </Col>
                <Col xs={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ยี่ห้อ / รุ่น
                  </Text>
                  <div>
                    <Text strong>
                      {detailDB2?.MORTGAGE?.TYPE} {detailDB2?.MORTGAGE?.MODEL}
                    </Text>
                  </div>
                </Col>
                <Col xs={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    เลขตัวถัง
                  </Text>
                  <div>{cleanChassis(detail?.chassis_no)}</div>
                </Col>
                <Col xs={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    เลขเครื่อง
                  </Text>
                  <div>{detailDB2?.MORTGAGE?.ENGNO || "-"}</div>
                </Col>
                <Col xs={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    สี
                  </Text>
                  <div>{detail?.color || "-"}</div>
                </Col>
                <Col xs={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ปีที่ผลิต
                  </Text>
                  <div>{detailDB2?.MORTGAGE?.MANUYR || "-"}</div>
                </Col>
                <Col xs={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ประเภท VAT
                  </Text>
                  <div>
                    {isVat ? (
                      <Tag color="red">VAT 7%</Tag>
                    ) : (
                      <Tag color="default">ไม่มี VAT</Tag>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
        {console.log(detail)}
        {/* ─── Form แก้ไขข้อมูล ─── */}
        <Divider>📝 แก้ไขข้อมูลก่อนพิมพ์</Divider>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            companyInvoice: 1,
            companyReceipt: 6,
            receiver_name: "ศุภมิตร  แก้วสุโพธิ์",
            doc_no: record?.doc_no,
            doc_dt: record?.doc_dt ? dayjs(record?.doc_dt) : null,
          }}

          //   onValuesChange={(changed) => {
          //     // แก้ "ราคารวม (รวม VAT)" → คำนวณ sellAmount + vat
          //     if ("sold_out_price" in changed) {
          //       const total = Number(changed.sold_out_price) || 0;
          //       const before = total / 1.07;
          //       form.setFieldsValue({
          //         sellAmount: currencyFormatPoint(before),
          //         vat: currencyFormatPoint(total - before),
          //       });
          //     }
          //     // แก้ "ราคาขาย (ก่อน VAT)" → คำนวณ vat + sold_out_price
          //     else if ("sellAmount" in changed) {
          //       const before = Number(changed.sellAmount) || 0;
          //       const total = before * 1.07;
          //       form.setFieldsValue({
          //         sold_out_price: currencyFormatPoint(total),
          //         vat: currencyFormatPoint(total - before),
          //       });
          //     }
          //     // แก้ "vat" → คำนวณ sold_out_price จาก sellAmount + vat
          //     else if ("vat" in changed) {
          //       const vatVal = Number(changed.vat) || 0;
          //       const before = form.getFieldValue("sellAmount") || 0;
          //       form.setFieldsValue({
          //         sold_out_price: currencyFormatPoint(before + vatVal),
          //       });
          //     }
          //   }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="เลือกบริษัทที่ออกใบกำกับ"
                name="companyInvoice"
                rules={[
                  { required: true, message: "เลือกบริษัทที่ออกใบกำกับ" },
                ]}
              >
                <Select
                  placeholder="เลือกบริษัท"
                  showSearch
                  optionFilterProp="label"
                  options={companiesOptions}
                  style={{ minWidth: 200 }}
                  size="large"
                  loading={loadingCompanies}
                  popupMatchSelectWidth={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="เลือกบริษัทที่ออกบิล"
                name="companyReceipt"
                rules={[{ required: true, message: "เลือกบริษัทที่ออกบิล" }]}
              >
                <Select
                  placeholder="เลือกบริษัท"
                  showSearch
                  optionFilterProp="label"
                  options={companiesOptions}
                  style={{ minWidth: 200 }}
                  size="large"
                  loading={loadingCompanies}
                  popupMatchSelectWidth={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="เลขที่ใบกำกับ"
                name="doc_no"
                rules={[{ required: true, message: "กรอกเลขที่ใบกำกับ" }]}
              >
                <Input
                  placeholder="เช่น MS2605120001"
                  value={record?.doc_no}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="วันที่ใบกำกับ"
                name="doc_dt"
                rules={[{ required: true, message: "เลือกวันที่" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  value={record?.doc_dt}
                  size="large"
                  disabled
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                label="ชื่อลูกค้า / บริษัท"
                name="buyer_name"
                rules={[{ required: true, message: "กรอกชื่อลูกค้า" }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="เลขประจำตัวผู้เสียภาษี" name="buyer_taxid">
                <Input maxLength={13} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="เบอร์โทร" name="buyer_phone">
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item label="ที่อยู่" name="buyer_address">
                <TextArea rows={2} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="ราคาขาย"
                name="sellAmount"
                rules={[{ required: true, message: "กรอกราคาขาย" }]}
              >
                <MoneyInput style={{ width: "100%" }} min={0} step={100} />
              </Form.Item>
            </Col>
            {isVat && (
              <Col xs={24} md={12}>
                <Form.Item
                  label="vat (7%)"
                  name="vat"
                  rules={[{ required: true, message: "กรอกราคาขาย" }]}
                >
                  <MoneyInput style={{ width: "100%" }} min={0} step={100} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={12}>
              <Form.Item
                label="ราคาขาย (รวม)"
                name="sold_out_price"
                rules={[{ required: true, message: "กรอกราคาขาย" }]}
              >
                <MoneyInput style={{ width: "100%" }} min={0} step={100} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item label="ชื่อผู้รับเงิน" name="receiver_name">
                <Input size="small" placeholder="ตัวอย่าง ขยัน ใจดี" />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item label="หมายเหตุ" name="mark">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {/* ─── ปุ่ม ─── */}
        <div style={{ marginTop: 12, textAlign: "right" }}>
          <Space>
            {saving && (
              <Button
                icon={<PrinterOutlined />}
                loading={printing}
                onClick={handlePrint}
              >
                พิมพ์ใบเสร็จ
              </Button>
            )}
            <Button
              danger
              onClick={() => {
                if (saving) {
                  console.log("saving", saving);
                  handleLoad?.();
                }
                handleClose();
              }}
              disabled={loading}
            >
              ปิด
            </Button>
            {!saving && (
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                loading={printing}
                onClick={handleSave}
              >
                บันทึก
              </Button>
            )}
          </Space>
        </div>
      </Spin>
    </Modal>
  );
};

export default InvoiceAndReceiptCarImpound;
